import type {
  CmsSectionCreateInput,
  CmsSectionUpdateInput,
} from "@/modules/cms/domain/contracts";
import {
  Button,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import type { CmsSectionDTO } from "../api/client";

const SectionFormSchema = z.object({
  pageKey: z.string().min(1, "ຕ້ອງໃສ່ page key"),
  sectionKey: z.string().min(1, "ຕ້ອງໃສ່ section key"),
  contentJson: z
    .string()
    .optional()
    .refine(
      (v) => {
        if (!v?.trim()) return true;
        try {
          JSON.parse(v);
          return true;
        } catch {
          return false;
        }
      },
      { message: "Content ຕ້ອງເປັນ JSON ທີ່ຖືກຕ້ອງ" },
    ),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type SectionFormValues = z.output<typeof SectionFormSchema>;

type SectionFormProps = {
  mode: "create" | "edit";
  pageKey: string;
  section?: CmsSectionDTO;
  onSubmit: (values: CmsSectionCreateInput | CmsSectionUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

export function SectionForm({
  mode,
  pageKey,
  section,
  onSubmit,
  onCancel,
  submitting,
}: SectionFormProps) {
  const defaultValues: SectionFormValues = {
    pageKey: section?.pageKey ?? pageKey,
    sectionKey: section?.sectionKey ?? "",
    contentJson:
      section?.content != null ? JSON.stringify(section.content, null, 2) : "",
    sortOrder: section?.sortOrder ?? 0,
  };

  const methods = RHF.useForm<SectionFormValues>({
    resolver: zodResolver(SectionFormSchema) as Resolver<SectionFormValues>,
    defaultValues,
  });

  const handleSubmit = (vals: SectionFormValues) => {
    let content: Record<string, unknown> | undefined;
    try {
      content = vals.contentJson?.trim()
        ? (JSON.parse(vals.contentJson) as Record<string, unknown>)
        : undefined;
    } catch {
      return; // validation จะจับ
    }
    if (mode === "create") {
      onSubmit({
        pageKey: vals.pageKey,
        sectionKey: vals.sectionKey,
        content,
        sortOrder: vals.sortOrder,
      });
    } else {
      onSubmit({
        sectionKey: vals.sectionKey,
        content: content ?? null,
        sortOrder: vals.sortOrder,
      });
    }
  };

  return (
    <FormRoot<SectionFormValues>
      methods={methods as UseFormReturn<SectionFormValues>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <FormInput
          name="pageKey"
          label="Page Key"
          placeholder="home"
          disabled={mode === "edit"}
        />
        <FormInput
          name="sectionKey"
          label="Section Key"
          placeholder="hero, features, ..."
        />
        <FormInput name="sortOrder" label="ລຳດັບ" type="number" min={0} />
        <FormTextarea
          name="contentJson"
          label="Content (JSON)"
          rows={10}
          placeholder='{"title": {"lo": "ສະບາຍດີ"}, "subtitle": {}}'
          className="font-mono text-sm"
        />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
